<template>
  <div class="flex flex-col w-full h-full">
    <!-- Top section with warehouse selector -->
    <div class="flex mb-4 w-full">
      <div class="field mr-4">
        <label
          for="warehouse"
          class="block text-sm font-medium mb-1"
        >Warehouse</label>
        <Dropdown
          id="warehouse"
          v-model="selectedWarehouse"
          :options="warehouses"
          option-label="name" 
          placeholder="Select Warehouse"
          class="w-full md:w-20rem"
        />
      </div>
            
      <!-- Search input -->
      <div class="field flex-grow-1">
        <label
          for="search"
          class="block text-sm font-medium mb-1"
        >Search Assets</label>
        <IconField class="w-full">
          <InputIcon class="pi pi-search" />
          <InputText
            id="search"
            v-model="searchQuery"
            placeholder="Search by name, ID, or category..."
            class="w-full"
          />
        </IconField>
      </div>
    </div>

    <div class="flex flex-col md:flex-row w-full gap-3 flex-1 overflow-hidden">
      <!-- LEFT COLUMN: Category Tree -->
      <div class="w-full md:w-1/4 mb-3 md:mb-0 overflow-hidden flex flex-col">
        <div class="border border-gray-200 rounded-lg shadow-sm h-full flex flex-col">
          <div class="p-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
            <h3 class="text-lg font-semibold m-0">
              Categories
            </h3>
            <Button 
              v-if="Object.keys(currentFilters).length > 0"
              v-tooltip.top="'Clear filters'" 
              icon="pi pi-times" 
              text 
              severity="secondary"
              class="p-button-sm"
              @click="clearFilters"
            />
          </div>
          <div class="overflow-auto flex-1">
            <TreeTable
              :value="categoryTree" 
              :expanded-keys="expandedKeys" 
              class="category-tree p-treetable-sm" 
              style="min-width: 100%"
              @nodeExpand="onNodeExpand"
              @nodeCollapse="onNodeCollapse"
            >
              <Column
                field="name"
                header="Name"
                expander
              >
                <template #body="{ node }">
                  <div
                    class="flex items-center"
                    @click="onNodeClick(node)"
                  >
                    <i
                      :class="node.data.icon"
                      :style="{ color: node.data.color }"
                      class="mr-2 text-gray-600"
                    />
                    <span>{{ node.data.name }}</span>
                    <Badge
                      v-if="node.data.count"
                      :value="node.data.count"
                      severity="info"
                      class="ml-2"
                    />
                  </div>
                </template>
              </Column>
            </TreeTable>
          </div>
        </div>
      </div>

      <!-- MIDDLE COLUMN: Asset Table -->
      <div class="w-full md:w-5/12 mb-3 md:mb-0 overflow-hidden flex flex-col">
        <div class="border border-gray-200 rounded-lg shadow-sm h-full flex flex-col">
          <div class="p-3 bg-gray-50 border-b border-gray-200">
            <h3 class="text-lg font-semibold m-0">
              Available Assets
            </h3>
          </div>
          <div class="overflow-auto flex-1">
            <DataTable
              :value="filteredItems"
              sort-field="name"
              :row-style="rowStyle"
              :row-class="rowClass"
              striped-rows
              :loading="isLoadingAssets"
              class="p-datatable-sm"
              style="min-width: 100%"
              @row-click="onRowClick"
            >
              <template #empty>
                <DataTableEmptyState
                  :is-loading="isLoadingAssets"
                  :error="assetsError"
                  loading-message="Loading assets..."
                  empty-message="No assets match your criteria."
                  :empty-hint="items.length === 0 ? 'No assets available.' : 'Try adjusting your search or selecting a different category.'"
                  empty-icon="pi pi-box"
                />
              </template>
              <Column
                field="name"
                sortable
              >
                <template #header>
                  <div class="pl-3">
                    Asset Name
                  </div>
                </template>
                <template #body="{ data }">
                  <div class="pl-3">
                    <span class="font-medium">{{ data.name }}</span>
                  </div>
                </template>
              </Column>
              <Column
                field="qty"
                style="width: 80px"
              >
                <template #header>
                  <div class="pl-3">
                    Available
                  </div>
                </template>
                <template #body="{ data }">
                  <div class="flex items-center pl-3">
                    <span :class="getAvailabilityClass(data.qty)">{{ data.qty }}</span>
                    <i
                      v-if="data.qty <= 0"
                      v-tooltip.top="'Out of stock'" 
                      class="pi pi-exclamation-triangle text-yellow-500 ml-2"
                    />
                  </div>
                </template>
              </Column>
              <Column
                style="width: 65px"
              >
                <template #header>
                  <div class="pl-3">
                    Qty
                  </div>
                </template>
                <template #body="{ data }">
                  <div class="flex items-center pl-3">
                    <input
                      type="number"
                      :value="data.qtyToAdd || 0"
                      :min="0"
                      :max="data.qty > 0 ? data.qty : 0"
                      :disabled="data.qty <= 0"
                      class="w-16 px-2 py-1 text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style="width: 60px;"
                      @input="(event) => selectRow(data, event.target.value)"
                    >
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN: Selected Assets Panel -->
      <SelectionBasketPanel
        title="Selected Assets"
        :items="selectedAssets"
        :total-count="getSelectedAssetsCount()"
        :formatted-total-amount="'£' + calculateTotal().toFixed(2)"
        total-count-label="Total Items"
        total-amount-label="Total Amount"
        add-button-loading-label="Adding Assets..."
        :view-mode="selectedViewMode"
        :is-submitting="isSubmitting"
        empty-message="No assets selected"
        empty-hint="Select assets from the table to add them here"
        @clear="clearSelectedAssets"
        @toggle-view="toggleSelectedViewMode"
        @save-and-close="saveAssetsAndClose"
        @save-and-keep-open="saveAssetsAndKeepOpen"
        @remove="removeSelectedAsset"
      >
        <template #detailed="{ item: asset, remove }">
          <div class="flex align-items-center justify-between mb-3">
            <div class="font-medium">
              {{ asset.name }}
            </div>
            <Button
              icon="pi pi-times"
              text
              rounded
              severity="danger"
              class="p-button-sm"
              @click="remove"
            />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div class="field">
              <label class="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
              <InputNumber
                v-model="asset.qtyToAdd"
                :min="1"
                :max="asset.qty || 999"
                class="w-full"
                size="small"
                @update:model-value="markAsChanged"
              />
            </div>
            <div class="field">
              <label class="block text-xs font-medium text-gray-700 mb-1">Days</label>
              <InputNumber
                v-model="asset.days"
                :min="1"
                :max="365"
                class="w-full"
                size="small"
                @update:model-value="markAsChanged"
              />
            </div>
            <div class="field">
              <label class="block text-xs font-medium text-gray-700 mb-1">Price (GBP)</label>
              <InputNumber
                v-model="asset.price"
                mode="currency"
                currency="GBP"
                locale="en-GB"
                :min="0"
                :max="999999"
                class="w-full"
                size="small"
                @update:model-value="markAsChanged"
              />
            </div>
            <div class="field">
              <label class="block text-xs font-medium text-gray-700 mb-1">Discount (%)</label>
              <InputNumber
                v-model="asset.discount"
                :min="0"
                :max="100"
                suffix="%"
                class="w-full"
                size="small"
                @update:model-value="markAsChanged"
              />
            </div>
          </div>
          <div class="mt-3 pt-2 border-t border-gray-100">
            <div class="flex justify-between items-center">
              <span class="text-sm font-medium">Total:</span>
              <span class="font-bold text-green-600">
                £{{ calculateAssetTotal(asset).toFixed(2) }}
              </span>
            </div>
          </div>
        </template>
      </SelectionBasketPanel>
    </div>

    <!-- Bottom Action -->
    <div class="flex justify-end mt-3">
      <Button
        label="Cancel"
        icon="pi pi-times"
        text
        @click="closeDialog"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineEmits } from "vue";
import { projectStore } from "../projectStore";
import { useToastService } from '@/composables/useToastService';
import SelectionBasketPanel from '@/components/shared/SelectionBasketPanel.vue';
import DataTableEmptyState from '@/components/shared/DataTableEmptyState.vue';

// Define emits
const emit = defineEmits(['close']);

// Toast service
const toast = useToastService();
import { getLocalAssets, addEquipmentListContent, getEquipmentListContent } from '@/services/projectService';
import { fetchClassificationStructure } from '@/services/companyClassificationsService';
import { LocalAssetService } from '@/services/localAssetService';

// Sample warehouse data
const warehouses = ref([
    { id: 1, name: 'Main Warehouse' },
    { id: 2, name: 'Studio Warehouse' },
    { id: 3, name: 'Offsite Storage' }
]);

const selectedWarehouse = ref(warehouses.value[0]);

// Category tree data
const categoryTree = ref([]);
const expandedKeys = ref({});
const selectedCategory = ref(null);

// Helper functions to get appropriate icons and colors based on type
const getIconForType = (type) => {
    switch (type) {
        case 'department':
            return 'pi pi-folder';
        case 'master_category':
            return 'pi pi-folder-open';
        case 'category':
            return 'pi pi-tag';
        default:
            return 'pi pi-folder';
    }
};

const getColorForType = (type) => {
    switch (type) {
        case 'department':
            return '#3B82F6'; // Blue
        case 'master_category':
            return '#F59E0B'; // Amber
        case 'category':
            return '#10B981'; // Green
        default:
            return '#6B7280'; // Gray
    }
};

// Transform nested API data to TreeTable format (copied from AddAccessoryModal)
const transformNestedDataToTree = (departments) => {
    return departments.map(dept => ({
        key: `dept_${dept.id}`,
        data: {
            name: dept.name,
            icon: getIconForType('department'),
            color: getColorForType('department'),
            count: dept.master_categories?.length || 0,
            type: 'department',
            level: 1,
            is_active: dept.pivot?.is_active || true,
            sort_order: dept.pivot?.sort_order || 0
        },
        children: dept.master_categories?.map(mc => ({
            key: `mc_${mc.id}`,
            data: {
                name: mc.name,
                icon: getIconForType('master_category'),
                color: getColorForType('master_category'),
                count: mc.categories?.length || 0,
                type: 'master_category',
                level: 2,
                is_active: mc.pivot?.is_active || true,
                sort_order: mc.pivot?.sort_order || 0
            },
            children: mc.categories?.map(cat => ({
                key: `cat_${cat.id}`,
                data: {
                    name: cat.name,
                    icon: getIconForType('category'),
                    color: getColorForType('category'),
                    count: 0,
                    type: 'category',
                    level: 3,
                    is_active: cat.pivot?.is_active || true,
                    sort_order: cat.pivot?.sort_order || 0
                },
                children: []
            })) || []
        })) || []
    }));
};

// Assets data from API
const items = ref([]);
const isLoadingAssets = ref(true);
const assetsError = ref(null);

// State variables
const searchQuery = ref("");
const selectedAssets = ref([]);
const selectedViewMode = ref('simple'); // 'detailed' | 'simple'
const currentFilters = ref({});
const isSubmitting = ref(false);

// Equipment list date range for availability (scoped to current list)
const equipmentListDates = ref({ start_date: null, end_date: null });

// Handle node click (copied from AddAccessoryModal)
const onNodeClick = async (node) => {
    if (!node || !node.key) return;

    // When a category is selected, update the selectedCategory
    selectedCategory.value = { [node.key]: true };

    // Build filters based on selection
    const nodeType = node.data?.type;
    const filters = {};

    if (nodeType === 'department') {
        const departmentId = parseInt(node.key.replace('dept_', ''));
        filters.department_id = departmentId;
    } else if (nodeType === 'master_category') {
        const masterCategoryId = parseInt(node.key.replace('mc_', ''));
        filters.master_category_id = masterCategoryId;
    } else if (nodeType === 'category') {
        const categoryId = parseInt(node.key.replace('cat_', ''));
        filters.category_id = categoryId;
    }

    // Update current filters and fetch assets
    currentFilters.value = filters;
    await fetchAssets(filters);
};

// Clear all filters
const clearFilters = async () => {
    
    // Reset selected category
    selectedCategory.value = null;
    
    // Clear current filters
    currentFilters.value = {};
    
    // Fetch all assets without filters
    await fetchAssets({});
};

// Handle tree node expansion
const onNodeExpand = (node) => {
    expandedKeys.value[node.key] = true;
};

// Handle tree node collapse
const onNodeCollapse = (node) => {
    delete expandedKeys.value[node.key];
};

// Load equipment list start/end dates for availability scope
const loadEquipmentListDates = async () => {
    const projectId = projectStore.currentProjectId;
    const equipmentListId = projectStore.currentEquipmentListId;
    if (!projectId || !equipmentListId) return;
    try {
        const res = await getEquipmentListContent(projectId, equipmentListId);
        const list = res.data;
        if (list && (list.start_date || list.end_date)) {
            const start = list.start_date ? new Date(list.start_date).toISOString().slice(0, 10) : null;
            const end = list.end_date ? new Date(list.end_date).toISOString().slice(0, 10) : null;
            if (start && end) equipmentListDates.value = { start_date: start, end_date: end };
        }
    } catch (err) {
        console.error('[AddAssetModal] Error loading equipment list dates:', err);
    }
};

// Fetch assets with optional filters, then load actual availability for the equipment list date range
const fetchAssets = async (filters = {}) => {
    try {
        isLoadingAssets.value = true;
        assetsError.value = null;

        const assetsResponse = await getLocalAssets(filters);

        if (assetsResponse && assetsResponse.data) {
            const assetsData = assetsResponse.data;

            // Transform assets data; qty will be set from availability API
            items.value = assetsData.map(asset => ({
                id: asset.id,
                name: asset.display_name || asset.custom_name || 'Unnamed Asset',
                qty: 0,
                price: asset.price || 0,
                barcode: asset.id.toString(),
                category: {
                    id: asset.category?.id,
                    name: asset.category?.name,
                    department: asset.department,
                    masterCategory: asset.master_category
                },
                imageUrl: '/placeholder-image.jpg',
                qtyToAdd: 0
            }));

            // Load equipment list dates if not yet loaded
            if (!equipmentListDates.value.start_date || !equipmentListDates.value.end_date) {
                await loadEquipmentListDates();
            }

            const { start_date, end_date } = equipmentListDates.value;
            if (start_date && end_date && items.value.length > 0) {
                try {
                    const availRes = await LocalAssetService.checkAvailabilityMultiple({
                        start_date,
                        end_date,
                        assets: items.value.map(a => ({ local_asset_id: a.id, quantity: 1 }))
                    });
                    const payload = availRes.data;
                    const assetsList = payload?.assets ?? [];
                    const byId = Object.fromEntries(assetsList.map(a => [a.local_asset_id, a]));
                    items.value.forEach(item => {
                        const avail = byId[item.id]?.availability?.availability_summary;
                        if (avail && typeof avail.available_for_confirmed_only === 'number') {
                            item.qty = avail.available_for_confirmed_only;
                        }
                    });
                } catch (availErr) {
                    console.error('[AddAssetModal] Error fetching availability:', availErr);
                }
            }
        } else {
            items.value = [];
        }

        isLoadingAssets.value = false;
    } catch (error) {
        console.error('Error fetching assets:', error);
        items.value = [];
        assetsError.value = 'Failed to load assets. Please try again.';
        isLoadingAssets.value = false;
    }
};

// Computed properties for filtering
const filteredItems = computed(() => {
    if (!items.value) return [];
    
    let filtered = items.value;
    
    // Filter by search term only (category filtering is now done via API)
    if (searchQuery.value) {
        const term = searchQuery.value.toLowerCase();
        filtered = filtered.filter(item => 
            item.name.toLowerCase().includes(term) || 
            item.barcode.toLowerCase().includes(term)
        );
    }
    
    return filtered;
});

// Select row and update quantity
const selectRow = (data, qty) => {
    const quantity = Number(qty) || 0;

    if (!quantity || quantity <= 0) {
        selectedAssets.value = selectedAssets.value.filter(asset => asset.id !== data.id);
        data.qtyToAdd = 0;
        return;
    }

    data.qtyToAdd = quantity;

    const existingIndex = selectedAssets.value.findIndex(asset => asset.id === data.id);

    if (existingIndex >= 0) {
        selectedAssets.value[existingIndex].qtyToAdd = quantity;
    } else {
        selectedAssets.value.push({
            id: data.id,
            name: data.name,
            price: data.price || 0,
            qty: data.qty,
            barcode: data.barcode,
            category: data.category,
            imageUrl: data.imageUrl,
            qtyToAdd: quantity,
            days: 3,
            discount: 0
        });
    }
};

// Save selected assets to the project
const saveAssets = async () => {
    if (isSubmitting.value) return;
    if (!selectedAssets.value || selectedAssets.value.length === 0) return;

    try {
        isSubmitting.value = true;
        await performSave();
        closeDialog();
    } catch (error) {
        console.error('Error adding assets to equipment list:', error);
        
        // Show error toast using PrimeVue toast service
        toast.add({
            severity: 'error',
            summary: 'Error Adding Assets',
            detail: error.response?.data?.message || error.message || 'Failed to add assets. Please try again.',
            life: 5000
        });
    } finally {
        isSubmitting.value = false;
    }
};

// Close the dialog
const closeDialog = () => {
    clearSelectedAssets();
    isSubmitting.value = false;
    emit('close');
};

// Toggle between detailed and simple view for selected assets
const toggleSelectedViewMode = () => {
    selectedViewMode.value = selectedViewMode.value === 'detailed' ? 'simple' : 'detailed';
};

// Perform save to API + refresh + success toast (no close/clear)
const performSave = async () => {
    const projectId = projectStore.currentProjectId;
    const equipmentListId = projectStore.currentEquipmentListId;
    const parentGroupId = projectStore.currentDraggableItemId;

    if (!projectId || !equipmentListId) {
        throw new Error('Project ID or Equipment List ID not found');
    }

    const promises = selectedAssets.value.map(async (asset) => {
        const contentData = {
            type: "item",
            name: asset.name,
            asset_type: "local_asset",
            local_asset_id: asset.id,
            quantity: asset.qtyToAdd,
            days: asset.days || 3,
            price: asset.price || 0,
            discount: asset.discount || 0,
            notes: "",
            parent_id: parentGroupId ? parseInt(parentGroupId) : null
        };
        return await addEquipmentListContent(projectId, equipmentListId, contentData);
    });

    await Promise.all(promises);

    if (projectStore.refreshFunction) {
        await projectStore.refreshFunction();
    }

    toast.add({
        severity: 'success',
        summary: 'Assets Added',
        detail: `${getSelectedAssetsCount()} assets added to the project`,
        life: 3000
    });
};

// Save basket and close (same as Add to Project)
const saveAssetsAndClose = () => {
    saveAssets();
};

// Save basket and keep window open with items emptied
const saveAssetsAndKeepOpen = async () => {
    if (isSubmitting.value || !selectedAssets.value?.length) return;
    isSubmitting.value = true;
    try {
        await performSave();
        clearSelectedAssets();
    } catch (error) {
        console.error('Error adding assets to equipment list:', error);
        toast.add({
            severity: 'error',
            summary: 'Error Adding Assets',
            detail: error.response?.data?.message || error.message || 'Failed to add assets. Please try again.',
            life: 5000
        });
    } finally {
        isSubmitting.value = false;
    }
};

// Clear all selected assets
const clearSelectedAssets = () => {
    // Reset quantities in the table
    filteredItems.value.forEach(item => {
        if (item.qtyToAdd) item.qtyToAdd = 0;
    });

    // Clear the selected assets array
    selectedAssets.value = [];
};

// Get total selected assets count
const getSelectedAssetsCount = () => {
    return selectedAssets.value.reduce((total, asset) => total + asset.qtyToAdd, 0);
};

// Row styling for availability status
const rowStyle = (data) => {
    if (data.qty < 0) {
        return {
            backgroundColor: "#FEF2F2",
            color: "#991B1B"
        };
    } else if (data.qty === 0) {
        return {
            backgroundColor: "#FFFBEB",
            color: "#92400E"
        };
    }
    return null;
};

const rowClass = (data) => {
    return {
        'cursor-pointer hover:bg-gray-50': true,
        'opacity-60': data.qty <= 0
    };
};

// Get class for availability display
const getAvailabilityClass = (qty) => {
    if (qty < 0) return 'text-red-600 font-medium';
    if (qty === 0) return 'text-yellow-600 font-medium';
    if (qty < 5) return 'text-orange-500 font-medium'; 
    return 'text-green-600 font-medium';
};

// Preselect a row on mount (optional)
const onRowClick = (event) => {
    // Only allow selecting items with available quantity
    if (event.data.qty <= 0) {
        return;
    }
    
    // Set default quantity to 1 if not already set
    if (!event.data.qtyToAdd) {
        event.data.qtyToAdd = 1;
    }
    
    // Select the row
    selectRow(event.data, event.data.qtyToAdd);
};

// Remove a selected asset
const removeSelectedAsset = (asset) => {
    selectedAssets.value = selectedAssets.value.filter(a => a.id !== asset.id);
    
    // Also reset the qtyToAdd in the filteredItems
    const item = filteredItems.value.find(item => item.id === asset.id);
    if (item) {
        item.qtyToAdd = 0;
    }
};

// Calculate total for individual asset (with discount)
const calculateAssetTotal = (asset) => {
    const qty = asset.qtyToAdd || 0;
    const price = asset.price || 0;
    const discount = asset.discount || 0;
    
    const subtotal = qty * price;
    const discountAmount = (subtotal * discount) / 100;
    return subtotal - discountAmount;
};

// Calculate total for all assets
const calculateTotal = () => {
    return selectedAssets.value.reduce((total, asset) => {
        return total + calculateAssetTotal(asset);
    }, 0);
};

// Mark as changed (for reactive updates)
const markAsChanged = () => {
    // This function can be used for any change tracking if needed
};

// Load categories function (copied from AddAccessoryModal)
const loadCategories = async () => {
    try {
        // Load categories from the structure API with show_all=false
        const response = await fetchClassificationStructure({ show_all: false });

        if (response.success && response.data) {
            const data = response.data;
            categoryTree.value = transformNestedDataToTree(data.departments);
        } else {
            categoryTree.value = [];
        }
        
    } catch (error) {
        console.error('Error loading categories:', error);
        categoryTree.value = [];
    }
};

// Initialize data (copied from AddAccessoryModal)
onMounted(async () => {
    await Promise.all([
        loadCategories(),
        fetchAssets() // Load all assets initially
    ]);
});
</script>

<style scoped>
/* Style the tree table */
:deep(.category-tree) {
    border: none;
}

:deep(.category-tree .p-treetable-header) {
    display: none;
}

:deep(.p-treetable-tbody > tr) {
    cursor: pointer;
}

:deep(.p-treetable-tbody > tr.p-highlight) {
    background-color: var(--primary-50);
}
</style>
